import React, { useEffect, useRef, useState } from 'react';

import OpterraPhoenixLogo from 'assets/images/OpterraPhoenixLogo.png';
import Notification from 'components/common/Notification';
import { useAsymptoticProgress } from 'hooks/useAsymptoticProgress';

interface OrganisationWorkspacePlaceholderProps {
  message?: string;
  completionFlags?: boolean[];
  /** JSON char count per API (same order as completionFlags). */
  dataSizes?: number[];
}

// ── Linear-interpolation curve constants ──

/** Duration (ms) to linearly reach a target value (rise or fall) */
const APPROACH_MS = 800;

/** How long (ms) to hold at the peak before decaying toward 0 */
const HOLD_MS = 300;

/** Idle ramp speed (chars / s) — constant slope before any API returns */
const RAMP_SLOPE = 500;

/** Cap for the idle ramp */
const MAX_IDLE_RAMP = 5000;

/** Amplitude of slow-pulse bumps shown when no API is responding (chars).
 *  Kept well below MAX_IDLE_RAMP so the bumps read as "very slow, still alive"
 *  rather than "normal throughput". */
const SLOW_PULSE_MAX = 400;

/** Rise / fall speed for slow-pulse bumps (chars / s — 30 % of RAMP_SLOPE). */
const SLOW_PULSE_SLOPE = RAMP_SLOPE * 0.3;

/** Sliding window width (ms) — only the last N ms are visible */
const WINDOW_MS = 1000;

const FADE_DELAY_MS = 400;

// ── Helpers ──

interface ThroughputEvent {
  time: number;
  size: number;
}

const formatAxisSize = (chars: number): string => {
  if (chars === 0) return '0';
  if (chars < 1024) return `${Math.round(chars)} B`;
  const kb = chars / 1024;
  if (kb < 1024)
    return kb >= 10 ? `${Math.round(kb)} KB` : `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
};

const formatTime = (ms: number): string => {
  if (ms === 0) return '0';
  const sec = ms / 1000;
  return sec === Math.floor(sec) ? `${sec}s` : `${sec.toFixed(1)}s`;
};

const computeXTickInterval = (): number => {
  return 250;
};

// ── Canvas margins ──
const MARGIN_LEFT = 46;
const MARGIN_BOTTOM = 18;
const MARGIN_RIGHT = 8;
const MARGIN_TOP = 4;

// ── Component ──

const OrganisationWorkspacePlaceholder: React.FC<
  OrganisationWorkspacePlaceholderProps
> = ({
  message = 'Make a selection to view in workspace',
  completionFlags,
  dataSizes,
}) => {
  const hasProgress =
    completionFlags !== undefined && completionFlags.length > 0;

  const { percentage, elapsed, isComplete } = useAsymptoticProgress(
    hasProgress ? completionFlags : []
  );

  const [showMessage, setShowMessage] = useState(!hasProgress);

  useEffect(() => {
    if (!hasProgress) {
      setShowMessage(true);
      return;
    }
    if (isComplete) {
      const timer = setTimeout(() => setShowMessage(true), FADE_DELAY_MS);
      return () => clearTimeout(timer);
    }
    setShowMessage(false);
  }, [hasProgress, isComplete]);

  // ── Stateful curve refs ──
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointsRef = useRef<Array<[number, number]>>([]);
  const eventsRef = useRef<ThroughputEvent[]>([]);
  const prevSizesRef = useRef<number[]>([]);
  const successColorRef = useRef('');
  const axisColorRef = useRef('');
  const labelColorRef = useRef('');
  const boldLabelColorRef = useRef('');
  const gridColorRef = useRef('');
  const canvasFontRef = useRef('');

  // Linear-interpolation state machine
  const currentYRef = useRef(0);
  const velocityRef = useRef(RAMP_SLOPE / 1000); // chars per ms
  const phaseTargetRef = useRef<number | null>(null); // null = idle ramp/decay
  const holdEndRef = useRef(0);
  const processedEventsRef = useRef(0);
  const lastElapsedRef = useRef(0);
  const smoothYMaxRef = useRef(1024 * 1.15);
  // true while idle is decaying back toward 0 (after hitting MAX_IDLE_RAMP)
  const idleDecayRef = useRef(false);
  // true while in slow-pulse mode: tiny repeated bumps (0 → SLOW_PULSE_MAX → 0)
  // shown when no API has responded recently, to signal "still alive but slow".
  const slowPulseRef = useRef(false);

  // Reset when re-entering loading state
  const wasMessageRef = useRef(showMessage);
  if (wasMessageRef.current && !showMessage) {
    pointsRef.current = [];
    eventsRef.current = [];
    prevSizesRef.current = [];
    currentYRef.current = 0;
    velocityRef.current = RAMP_SLOPE / 1000;
    phaseTargetRef.current = null;
    holdEndRef.current = 0;
    processedEventsRef.current = 0;
    lastElapsedRef.current = 0;
    smoothYMaxRef.current = 1024 * 1.15;
    idleDecayRef.current = false;
    slowPulseRef.current = false;
  }
  wasMessageRef.current = showMessage;

  // Detect new API completion events (render body, before effects)
  if (dataSizes && !showMessage) {
    dataSizes.forEach((size, i) => {
      const prev = prevSizesRef.current[i] ?? 0;
      if (size > 0 && prev === 0) {
        eventsRef.current.push({ time: elapsed, size });
      }
    });
    prevSizesRef.current = [...dataSizes];
  }

  const cumulativeBytes = dataSizes
    ? dataSizes.reduce((sum, s) => sum + s, 0)
    : 0;

  // ── Draw each frame ──
  useEffect(() => {
    if (showMessage) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Detect hook time-base reset (elapsed jumped backward) → flush stale data
    const lastPt = pointsRef.current[pointsRef.current.length - 1];
    if (lastPt && elapsed < lastPt[0] - 50) {
      pointsRef.current = [];
      eventsRef.current = [];
      prevSizesRef.current = [];
      currentYRef.current = 0;
      velocityRef.current = RAMP_SLOPE / 1000;
      phaseTargetRef.current = null;
      holdEndRef.current = 0;
      processedEventsRef.current = 0;
      lastElapsedRef.current = 0;
      smoothYMaxRef.current = 1024 * 1.15;
      idleDecayRef.current = false;
      slowPulseRef.current = false;
    }

    const frameDt = Math.max(elapsed - lastElapsedRef.current, 1);
    lastElapsedRef.current = elapsed;

    const events = eventsRef.current;

    // ── 1. Detect new API event → start approach ──
    if (events.length > processedEventsRef.current) {
      const latest = events[events.length - 1];
      processedEventsRef.current = events.length;
      phaseTargetRef.current = latest.size;
      const distance = latest.size - currentYRef.current;
      idleDecayRef.current = false;
      slowPulseRef.current = false; // real data → exit slow pulse immediately
      if (Math.abs(distance) < 1) {
        // Already at target — hold immediately so step 2 will decay to 0.
        velocityRef.current = 0;
        holdEndRef.current = elapsed + HOLD_MS;
      } else {
        velocityRef.current = distance / APPROACH_MS; // constant slope
        holdEndRef.current = 0;
      }
    }

    // ── 2. Check if hold period expired → start decay ──
    if (holdEndRef.current > 0 && elapsed >= holdEndRef.current) {
      holdEndRef.current = 0;
      phaseTargetRef.current = 0;
      velocityRef.current = -currentYRef.current / APPROACH_MS;
    }

    // ── 3. Move current Y linearly ──
    currentYRef.current += velocityRef.current * frameDt;

    // ── 4. Clamp & phase transitions ──
    if (phaseTargetRef.current !== null) {
      const t = phaseTargetRef.current;

      const reachedUp = velocityRef.current > 0 && currentYRef.current >= t;
      const reachedDown =
        velocityRef.current < 0 && currentYRef.current <= Math.max(t, 0);

      if (reachedUp) {
        // Reached peak → hold, then step 2 decays to 0.
        currentYRef.current = t;
        velocityRef.current = 0;
        holdEndRef.current = elapsed + HOLD_MS;
      } else if (reachedDown) {
        currentYRef.current = Math.max(t, 0);
        if (t <= 0) {
          // Decayed to 0 after real API event — enter slow pulse.
          // Tiny bumps signal "still waiting for more APIs, just slow".
          // Step 1 will override with a full spike when the next API responds.
          phaseTargetRef.current = null;
          idleDecayRef.current = false;
          slowPulseRef.current = true;
          velocityRef.current = SLOW_PULSE_SLOPE / 1000;
        } else {
          // Descended to a non-zero target (new event was smaller than previous
          // spike). Stop here, hold briefly, then step 2 will decay to 0.
          // Without this, velocity stays negative and Y oscillates at t forever.
          velocityRef.current = 0;
          holdEndRef.current = elapsed + HOLD_MS;
        }
      }
    }

    // ── 5. Idle ramp ceiling → decay, then slow pulse ────────────────────────
    // Normal idle: ramp from 0 to MAX_IDLE_RAMP, then decay back to 0.
    // On reaching 0, hand off to step 5.5 (slow pulse) rather than flat-lining.
    if (phaseTargetRef.current === null) {
      if (!idleDecayRef.current && !slowPulseRef.current && currentYRef.current >= MAX_IDLE_RAMP) {
        // Idle ramp hit ceiling → start decay at 4× rise speed so the
        // descending slope is clearly visible within the chart window.
        idleDecayRef.current = true;
        currentYRef.current = MAX_IDLE_RAMP;
        velocityRef.current = -(RAMP_SLOPE / 1000) * 4;
      } else if (idleDecayRef.current && !slowPulseRef.current && currentYRef.current <= 0) {
        // Idle decay hit 0 → enter slow pulse (step 5.5 takes over from here)
        currentYRef.current = 0;
        idleDecayRef.current = false;
        slowPulseRef.current = true;
        velocityRef.current = SLOW_PULSE_SLOPE / 1000;
      }
    }

    // ── 5.5. Slow-pulse cycling ───────────────────────────────────────────────
    // Small repeated bumps (0 → SLOW_PULSE_MAX → 0) while waiting with no API
    // response. Signals "still transmitting, just very slowly" instead of a
    // flat line. Step 1 overrides immediately when real data arrives.
    if (slowPulseRef.current && phaseTargetRef.current === null) {
      if (!idleDecayRef.current && currentYRef.current >= SLOW_PULSE_MAX) {
        // Reached slow-pulse ceiling → decay back to 0
        idleDecayRef.current = true;
        currentYRef.current = SLOW_PULSE_MAX;
        velocityRef.current = -(SLOW_PULSE_SLOPE / 1000);
      } else if (idleDecayRef.current && currentYRef.current <= 0) {
        // Back to 0 → restart the next small bump
        currentYRef.current = 0;
        idleDecayRef.current = false;
        velocityRef.current = SLOW_PULSE_SLOPE / 1000;
      }
    }

    // ── 6. Record data point (with jitter for visual noise) ──
    // Per-frame random jitter is intentional: high-frequency noise is averaged
    // out by the eye, so the underlying trend (ramp / decay) remains clearly
    // visible. Held jitter would create large visible steps that mask the trend.
    const baseY = currentYRef.current;
    const jitter = baseY > 1 ? (Math.random() - 0.5) * baseY * 0.15 : 0;
    pointsRef.current.push([elapsed, Math.max(0, baseY + jitter)]);

    // ── Resolve CSS token colours once ──
    if (!successColorRef.current) {
      const root = getComputedStyle(document.documentElement);
      const v = (name: string) => root.getPropertyValue(name).trim();
      successColorRef.current = v('--color-success') || '#00614f';
      axisColorRef.current = v('--color-secondary') || '#dbdada';
      labelColorRef.current = v('--color-scrollbar') || '#a6a5a5';
      boldLabelColorRef.current = v('--color-charcoal') || '#333333';
      gridColorRef.current = v('--color-bright-gray') || '#e5e9ec';
      const size = v('--font-xs') || '0.625rem';
      const family = v('--font-family') || "'Noto Sans', sans-serif";
      canvasFontRef.current = `${size} ${family}`;
    }
    const color = successColorRef.current;
    const axisColor = axisColorRef.current;
    const labelColor = labelColorRef.current;
    const boldLabelColor = boldLabelColorRef.current;
    const gridColor = gridColorRef.current;
    const canvasFont = canvasFontRef.current;
    const canvasBoldFont = `bold ${canvasFont}`;

    const dpr = window.devicePixelRatio || 1;
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    canvas.width = cw * dpr;
    canvas.height = ch * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    // ── Chart area ──
    const chartW = cw - MARGIN_LEFT - MARGIN_RIGHT;
    const chartH = ch - MARGIN_TOP - MARGIN_BOTTOM;

    const points = pointsRef.current;

    // X range — sliding window: show only the last WINDOW_MS
    const winRight = elapsed;
    const winLeft = Math.max(0, elapsed - WINDOW_MS);
    const winSpan = winRight - winLeft || 1;

    // Find first visible point (one before winLeft for line continuity)
    let firstVisible = 0;
    for (let i = points.length - 1; i >= 0; i--) {
      if (points[i][0] <= winLeft) {
        firstVisible = i;
        break;
      }
    }

    // Y range — smooth auto-scale to prevent sudden jumps
    let peakY = 1024;
    for (let i = firstVisible; i < points.length; i++) {
      if (points[i][1] > peakY) peakY = points[i][1];
    }
    const targetYMax = Math.max(peakY * 1.15, 1024 * 1.15);
    // Expand fast (≈10 frames), shrink slow (≈40 frames)
    const yFactor = targetYMax > smoothYMaxRef.current ? 0.15 : 0.03;
    smoothYMaxRef.current += (targetYMax - smoothYMaxRef.current) * yFactor;
    const yMax = smoothYMaxRef.current;

    // Coordinate helpers
    const toX = (t: number) => MARGIN_LEFT + ((t - winLeft) / winSpan) * chartW;
    const toY = (v: number) => MARGIN_TOP + chartH - (v / yMax) * chartH;

    // ── Axis lines ──
    ctx.strokeStyle = axisColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(MARGIN_LEFT, MARGIN_TOP);
    ctx.lineTo(MARGIN_LEFT, MARGIN_TOP + chartH);
    ctx.lineTo(MARGIN_LEFT + chartW, MARGIN_TOP + chartH);
    ctx.stroke();

    // ── Y-axis ticks & grid ──
    ctx.font = canvasFont;
    ctx.fillStyle = labelColor;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';

    for (let tick = 0; tick <= 4; tick++) {
      const yVal = (yMax * tick) / 4;
      const py = toY(yVal);

      ctx.strokeStyle = axisColor;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(MARGIN_LEFT - 4, py);
      ctx.lineTo(MARGIN_LEFT, py);
      ctx.stroke();

      ctx.fillText(formatAxisSize(yVal), MARGIN_LEFT - 6, py);

      if (tick > 0) {
        ctx.strokeStyle = gridColor;
        ctx.lineWidth = 0.5;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(MARGIN_LEFT + 1, py);
        ctx.lineTo(MARGIN_LEFT + chartW, py);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    // ── X-axis ticks (shift left; only show labels that fit) ──
    const xTickInterval = computeXTickInterval();
    const MIN_LABEL_GAP = 28; // px — minimum spacing between tick labels

    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    // Collect ticks within the visible window
    const xTicks: Array<{ t: number; px: number }> = [];
    const firstTick = Math.ceil(winLeft / xTickInterval) * xTickInterval;
    for (let t = firstTick; t <= winRight; t += xTickInterval) {
      const px = toX(t);
      if (px < MARGIN_LEFT - 1 || px > cw - MARGIN_RIGHT + 1) continue;
      xTicks.push({ t, px });
    }

    // Mark which ticks get a label (greedy from the right)
    const showLabel = new Array(xTicks.length).fill(false);
    let nextAllowedPx = Infinity;
    for (let i = xTicks.length - 1; i >= 0; i--) {
      if (xTicks[i].px + MIN_LABEL_GAP <= nextAllowedPx) {
        showLabel[i] = true;
        nextAllowedPx = xTicks[i].px;
      }
    }

    for (let i = 0; i < xTicks.length; i++) {
      const { t, px } = xTicks[i];

      // Always draw tick mark
      ctx.strokeStyle = axisColor;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(px, MARGIN_TOP + chartH);
      ctx.lineTo(px, MARGIN_TOP + chartH + 4);
      ctx.stroke();

      // Only draw label if it fits
      if (showLabel[i]) {
        const isWholeSec = t % 1000 === 0;
        ctx.font = isWholeSec ? canvasBoldFont : canvasFont;
        ctx.fillStyle = isWholeSec ? boldLabelColor : labelColor;
        ctx.fillText(formatTime(t), px, MARGIN_TOP + chartH + 5);
      }
    }

    // ── Throughput curve (visible window only) ──
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    for (let i = firstVisible; i < points.length; i++) {
      const px = toX(points[i][0]);
      const py = toY(points[i][1]);
      if (i === firstVisible) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();

    // ── Dot at current position ──
    const last = points[points.length - 1];
    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.arc(toX(last[0]), toY(last[1]), 3, 0, Math.PI * 2);
    ctx.fill();
  }, [percentage, elapsed, cumulativeBytes, showMessage]);

  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="flex flex-col items-center gap-4 text-center">
        <div
          className={`workspace-progress${!showMessage && isComplete ? ' workspace-progress--done' : ''}`}
        >
          {showMessage ? (
            <div className="flex w-full flex-col gap-3 text-left">
              <Notification
                type="primary"
                closable={false}
                icon={
                  <img
                    src={OpterraPhoenixLogo}
                    alt=""
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 flex-shrink-0 brightness-0 invert"
                  />
                }
              >
                <span className="text-sm font-medium leading-snug">
                  {message}
                </span>
              </Notification>
              <div className="flex flex-col gap-2 rounded-[var(--radius-button)] border border-gray-argent bg-white p-3">
                <div className="h-8 rounded-[var(--radius-button)] border border-dashed border-gray-argent" />
                <div className="flex gap-2">
                  <div className="h-3 w-2/5 rounded bg-secondary-light" />
                  <div className="h-3 flex-1 rounded bg-gray-bright" />
                </div>
                <div className="flex gap-2">
                  <div className="h-3 w-3/5 rounded bg-gray-bright" />
                  <div className="h-3 w-1/5 rounded bg-secondary-light" />
                </div>
              </div>
            </div>
          ) : (
            <>
              <span className="workspace-progress__label">
                Loading workspace...
              </span>
              <canvas ref={canvasRef} className="workspace-progress__canvas" />
              <div className="workspace-progress__bar">
                <div
                  className="workspace-progress__indicator"
                  style={{ width: `${Math.round(percentage)}%` }}
                />
              </div>
              {/* Debug display — uncomment to diagnose progress bar issues
              <span className="workspace-progress__label">
                pct={percentage.toFixed(1)}% | done=
                {completionFlags?.filter(Boolean).length ?? 0}/
                {completionFlags?.length ?? 0} | flags=[
                {completionFlags?.map((f) => (f ? '1' : '0')).join('') ??
                  '-'}
                ] | elapsed={Math.round(elapsed)}ms
              </span>
              */}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganisationWorkspacePlaceholder;
